import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Bell,
  BookOpen,
  ClipboardList,
  Droplets,
  Home,
  Ruler,
  Settings,
  Shield,
  Sparkles,
  Store,
  Tag,
  Tags,
  Users,
  Warehouse,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import type { ChannelSummary } from "../lib/types";
import { channelTitle } from "../lib/channel-labels";

export interface NavItemDef {
  to: string;
  icon: ReactNode;
  label: string;
  show: boolean;
  end?: boolean;
  badge?: number;
  active?: boolean;
}

export interface NavGroup {
  title: string;
  items: NavItemDef[];
  loading?: boolean;
}

/**
 * Grupos do menu — fonte unica consumida por Nav (desktop) e pela folha
 * "Mais" da TabBar (mobile), pra nunca divergirem sobre quais telas existem
 * e pra quem. "Historico de versoes" NAO entra aqui por decisao do plano de
 * redesign (secao 2.2 D2): e por-SKU, alcancado pelo botao Historico da
 * ficha, nao pelo menu global.
 */
export function useNavGroups(): NavGroup[] {
  const { can, isAuthenticated, hasAccessToken } = useAuth();
  const location = useLocation();

  const { data: channels = [], isLoading } = useQuery<ChannelSummary[]>({
    queryKey: ["channels"],
    queryFn: async () => {
      const r = await api.get<ChannelSummary[]>("/api/channels");
      return r.data;
    },
    enabled: isAuthenticated && hasAccessToken,
  });

  const groups: NavGroup[] = [
    {
      title: "Início",
      items: [
        { to: "/", icon: <Home className="icon" />, label: "Painel", show: true, end: true },
        { to: "/manual", icon: <BookOpen className="icon" />, label: "Manual do operador", show: true },
      ],
    },
    {
      title: "Canais",
      items: [
        ...channels.map((c) => ({
          to: `/canais/${c.slug}`,
          icon: <Store className="icon" />,
          label: channelTitle(c),
          badge: c.stats?.total,
          show: true,
          active: location.pathname.startsWith(`/canais/${c.slug}`),
        })),
        {
          to: "/admin/canais",
          icon: <Store className="icon" />,
          label: "Frentes & canais",
          show: can("channels.manage"),
        },
      ],
    },
    {
      title: "Administração",
      items: [
        { to: "/admin/categorias", icon: <Tag className="icon" />, label: "Categorias", show: can("categories.write") },
        { to: "/admin/tags", icon: <Tags className="icon" />, label: "Tags", show: can("tags.write") },
        { to: "/admin/tecnicidades", icon: <Sparkles className="icon" />, label: "Tecnicidades", show: can("technicalities.write") },
        { to: "/admin/lavagem", icon: <Droplets className="icon" />, label: "Instruções de lavagem", show: can("care_instructions.write") },
        { to: "/admin/guias-de-tamanho", icon: <Ruler className="icon" />, label: "Guias de tamanho", show: can("size_guides.write") },
        { to: "/admin/estoques", icon: <Warehouse className="icon" />, label: "Estoques", show: can("stock.read") },
        { to: "/admin/usuarios", icon: <Users className="icon" />, label: "Usuários", show: can("users.manage") },
        { to: "/admin/perfis", icon: <Shield className="icon" />, label: "Perfis e acessos", show: can("roles.manage") },
        { to: "/admin/settings", icon: <Settings className="icon" />, label: "Configurações", show: can("settings.manage") },
      ],
    },
    {
      title: "Monitoramento",
      items: [
        { to: "/admin/notificacoes", icon: <Bell className="icon" />, label: "Notificações", show: can("notifications.read") },
        { to: "/admin/audit", icon: <ClipboardList className="icon" />, label: "Log de auditoria", show: can("audit.read") },
      ],
    },
  ];

  const visibleGroups = groups
    .map((g) => ({ ...g, items: g.items.filter((i) => i.show) }))
    .filter((g) => g.items.length > 0);

  if (isLoading && channels.length === 0) {
    return visibleGroups.map((g) => (g.title === "Canais" ? { ...g, loading: true } : g));
  }
  return visibleGroups;
}
